import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "./api";
import { getToken, setToken, clearToken } from "./tokenStorage";

const AuthContext = createContext(null);

// Admin view override modes (applies solo se user.is_admin === true)
const VIEW_MODES = ["auto", "piva", "dipendente", "entrambi"];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminViewMode, setAdminViewModeState] = useState(() => {
    // Fix P2: leggi il flag solo se lo troviamo (non lo creiamo automaticamente).
    // Verrà comunque scritto SOLO quando setAdminViewMode viene chiamato (dall'admin toggle).
    const v = localStorage.getItem("tuotax_admin_view");
    return VIEW_MODES.includes(v) ? v : "auto";
  });

  const setAdminViewMode = (mode) => {
    if (!VIEW_MODES.includes(mode)) return;
    // Fix P2: scrivi il flag SOLO se l'utente è admin (impedisce di creare la chiave
    // in localStorage per utenti normali che non hanno modo di modificare il valore).
    if (user?.is_admin) {
      localStorage.setItem("tuotax_admin_view", mode);
    }
    setAdminViewModeState(mode);
  };

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        // Fix P2: se l'utente NON è admin, rimuovi il flag admin_view residuo (pulizia)
        if (!r.data?.is_admin) {
          localStorage.removeItem("tuotax_admin_view");
          setAdminViewModeState("auto");
        }
      })
      .catch(() => clearToken())
      .finally(() => setLoading(false));
  }, []);

  // effectiveUser: applica override della modalità admin sopra l'utente reale.
  // Solo se user.is_admin, altrimenti restituisce user tal quale.
  const effectiveUser = useMemo(() => {
    if (!user || !user.is_admin || adminViewMode === "auto") return user;
    if (adminViewMode === "piva") {
      return { ...user, tipo_piva: user.tipo_piva === "nessuna" ? "forfettario" : user.tipo_piva, has_dipendente_income: false };
    }
    if (adminViewMode === "dipendente") {
      return { ...user, tipo_piva: "nessuna", has_dipendente_income: true };
    }
    if (adminViewMode === "entrambi") {
      return { ...user, tipo_piva: user.tipo_piva === "nessuna" ? "forfettario" : user.tipo_piva, has_dipendente_income: true };
    }
    return user;
  }, [user, adminViewMode]);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    setToken(r.data.access_token);
    setUser(r.data.user);
    return r.data.user;
  };

  const register = async (payload) => {
    const r = await api.post("/auth/register", payload);
    setToken(r.data.access_token);
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = () => {
    clearToken();
    // Fix P2: cleanup completo admin flag (anche se admin — coerenza)
    localStorage.removeItem("tuotax_admin_view");
    setAdminViewModeState("auto");
    setUser(null);
  };

  const refresh = async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      return r.data;
    } catch {
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{
      user: effectiveUser,
      realUser: user,
      setUser,
      loading,
      login,
      register,
      logout,
      refresh,
      adminViewMode,
      setAdminViewMode,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
